import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import { ShareButton } from '../components/ShareCard';
import axios from 'axios';
import { Crown, Magnet, Star as StarIcon, PawPrint, Orbit, Users as UsersIcon, Heart as HeartIcon, Dna } from 'lucide-react';

const GunaIcon = ({ icon: Icon }) => (
  <span className="w-10 h-10 rounded-xl bg-[#FCE9D9] flex items-center justify-center shrink-0">
    {Icon && <Icon className="w-5 h-5 text-[#C84B32]" />}
  </span>
);
import { 
  Sparkles, ArrowLeft, Heart, Users, Star, Moon, Sun,
  Loader2, Check, AlertCircle, ChevronDown, RefreshCw, Download, Share2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Progress } from '../components/ui/progress';
import ShubhHeader from '../components/shubh/ShubhHeader';
import ShubhBottomNav from '../components/shubh/ShubhBottomNav';

import { BACKEND_URL } from '../lib/backend';

const API = `${BACKEND_URL}/api`;


const getGunaInfo = (language) => ({
  varna: { name: language === 'hi' ? "वर्ण" : "Varna", icon: Crown, description: language === 'hi' ? "सामाजिक और आध्यात्मिक सामंजस्य" : "Social & spiritual harmony" },
  vashya: { name: language === 'hi' ? "वश्य" : "Vashya", icon: Magnet, description: language === 'hi' ? "पारस्परिक आकर्षण और नियंत्रण" : "Mutual attraction & control" },
  tara: { name: language === 'hi' ? "तारा" : "Tara", icon: StarIcon, description: language === 'hi' ? "जन्म नक्षत्र भाग्य" : "Birth star destiny" },
  yoni: { name: language === 'hi' ? "योनि" : "Yoni", icon: PawPrint, description: language === 'hi' ? "शारीरिक अनुकूलता" : "Physical compatibility" },
  graha_maitri: { name: language === 'hi' ? "ग्रह मैत्री" : "Graha Maitri", icon: Orbit, description: language === 'hi' ? "मानसिक अनुकूलता" : "Mental compatibility" },
  gana: { name: language === 'hi' ? "गण" : "Gana", icon: UsersIcon, description: language === 'hi' ? "स्वभाव मेल" : "Temperament match" },
  bhakoot: { name: language === 'hi' ? "भकूट" : "Bhakoot", icon: HeartIcon, description: language === 'hi' ? "प्रेम और भावनाएं" : "Love & emotions" },
  nadi: { name: language === 'hi' ? "नाड़ी" : "Nadi", icon: Dna, description: language === 'hi' ? "स्वास्थ्य और संतान" : "Health & progeny" }
});

export const KundliMilanPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const { isDark } = useTheme();
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [profile1, setProfile1] = useState('');
  const [profile2, setProfile2] = useState('');
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [generatingPdf, setGeneratingPdf] = useState(false);
  
  const GUNA_INFO = getGunaInfo(language);

  useEffect(() => {
    fetchProfiles();
  }, []);

  const fetchProfiles = async () => {
    try {
      const response = await axios.get(`${API}/profiles`, { withCredentials: true });
      setProfiles(response.data);
    } catch (error) {
      console.error('Error fetching profiles:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkCompatibility = async () => {
    if (!profile1 || !profile2) {
      setError('Please select both profiles');
      return;
    }
    if (profile1 === profile2) {
      setError('Please select two different profiles');
      return;
    }

    setAnalyzing(true);
    setError('');
    setResult(null);

    try {
      const response = await axios.post(
        `${API}/compatibility/check`,
        { profile1_id: profile1, profile2_id: profile2 },
        { withCredentials: true }
      );
      setResult(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to analyze compatibility');
    } finally {
      setAnalyzing(false);
    }
  };

  const getInitials = (name) => {
    return name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || '?';
  };

  const getScoreColor = (percentage) => {
    if (percentage >= 80) return 'text-green-600';
    if (percentage >= 60) return 'text-[#C84B32]';
    if (percentage >= 40) return 'text-orange-600';
    return 'text-red-600';
  };

  const downloadPdf = async () => {
    if (!result) return;
    
    setGeneratingPdf(true);
    try {
      // Prepare compatibility data for PDF
      const compatibilityData = {
        total_score: result.guna_milan.total_score,
        max_score: 36,
        percentage: result.guna_milan.percentage,
        verdict: result.guna_milan.verdict,
        kootas: Object.entries(result.guna_milan.guna_details).map(([key, guna]) => ({
          name: GUNA_INFO[key]?.name || key,
          score: guna.points,
          max_score: guna.max_points
        })),
        analysis: result.ai_analysis.summary || `${result.ai_analysis.emotional_bond}\n\n${result.ai_analysis.communication}\n\n${result.ai_analysis.family_life}`
      };

      const response = await fetch(`${API}/kundli-milan/download-pdf`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          profile1_id: profile1,
          profile2_id: profile2,
          compatibility_data: compatibilityData
        })
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Kundli_Milan_${result.profile1.name.replace(/\s+/g, '_')}_${result.profile2.name.replace(/\s+/g, '_')}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
      } else {
        console.error('PDF download failed:', await response.text());
      }
    } catch (err) {
      console.error('Error downloading PDF:', err);
    } finally {
      setGeneratingPdf(false);
    }
  };

  const getProgressColor = (percentage) => {
    if (percentage >= 80) return 'bg-green-400';
    if (percentage >= 60) return 'bg-[#C84B32]';
    if (percentage >= 40) return 'bg-orange-400';
    return 'bg-red-400';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FFF9EF] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#C84B32] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FFF9EF] cosmic-bg pb-28">
      {/* Glow effects */}
      <div className="fixed top-1/4 left-1/4 w-[500px] h-[500px] bg-[#C84B32] rounded-full filter blur-[300px] opacity-10 pointer-events-none" />
      <div className="fixed bottom-1/4 right-1/4 w-[500px] h-[500px] bg-[#C84B32] rounded-full filter blur-[300px] opacity-10 pointer-events-none" />

      {/* Navigation */}
      <ShubhHeader title={t('milan.title')} showBack />

      <main className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="text-center mb-10">
          <p className="text-[#C84B32] text-xs uppercase tracking-[0.3em] mb-4">{t('milan.subtitle')}</p>
          <h1 className="font-['Fraunces'] text-4xl sm:text-5xl text-[#282622] mb-4">
            {t('milan.title')}
          </h1>
          <p className="text-[#6B6560] max-w-xl mx-auto">
            {language === 'hi' 
              ? 'प्राचीन वैदिक अष्टकूट गुण मिलान प्रणाली का उपयोग करके दो आत्माओं के बीच ब्रह्मांडीय अनुकूलता की खोज करें'
              : 'Discover the cosmic compatibility between two souls using the ancient Vedic Ashtakoot Guna Milan system'}
          </p>
        </div>

        {/* Profile Selection */}
        <div className="cosmic-card p-8 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-end">
            {/* Profile 1 */}
            <div className="space-y-3">
              <label className="text-[#6B6560] text-sm flex items-center gap-2">
                <Users className="w-4 h-4" /> {t('milan.selectPartner1')}
              </label>
              <Select value={profile1} onValueChange={setProfile1}>
                <SelectTrigger 
                  data-testid="select-profile1"
                  className="bg-white border-[#282622]/10 text-[#282622] h-14 rounded-xl"
                >
                  <SelectValue placeholder={language === 'hi' ? 'प्रोफाइल चुनें' : 'Select profile'} />
                </SelectTrigger>
                <SelectContent className="bg-white border-[#282622]/10">
                  {profiles.map((p) => (
                    <SelectItem 
                      key={p.profile_id} 
                      value={p.profile_id}
                      className="text-[#282622] focus:bg-[#FCE9D9]"
                    >
                      {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Heart Icon */}
            <div className="flex justify-center">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#C84B32]/20 to-[#F5B942]/20 flex items-center justify-center">
                <Heart className="w-8 h-8 text-[#C84B32]" />
              </div>
            </div>

            {/* Profile 2 */}
            <div className="space-y-3">
              <label className="text-[#6B6560] text-sm flex items-center gap-2">
                <Users className="w-4 h-4" /> {t('milan.selectPartner2')}
              </label>
              <Select value={profile2} onValueChange={setProfile2}>
                <SelectTrigger 
                  data-testid="select-profile2"
                  className="bg-white border-[#282622]/10 text-[#282622] h-14 rounded-xl"
                >
                  <SelectValue placeholder={language === 'hi' ? 'प्रोफाइल चुनें' : 'Select profile'} />
                </SelectTrigger>
                <SelectContent className="bg-white border-[#282622]/10">
                  {profiles.map((p) => (
                    <SelectItem 
                      key={p.profile_id} 
                      value={p.profile_id}
                      className="text-[#282622] focus:bg-[#FCE9D9]"
                    >
                      {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {error && (
            <div className="mt-6 p-4 rounded-xl bg-red-500/10 border border-red-500/20 text-red-600 text-sm flex items-center gap-2">
              <AlertCircle className="w-4 h-4" />
              {error}
            </div>
          )}

          <div className="mt-8 flex justify-center">
            <Button
              onClick={checkCompatibility}
              disabled={analyzing || !profile1 || !profile2}
              data-testid="check-compatibility-btn"
              className="bg-gradient-to-r from-[#C84B32] to-[#F5B942] text-white hover:from-[#C84B32]/90 hover:to-[#F5B942]/90 px-10 py-6 rounded-xl font-medium text-lg"
            >
              {analyzing ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin mr-2" />
                  {t('milan.checking')}
                </>
              ) : (
                <>
                  <Sparkles className="w-5 h-5 mr-2" />
                  {t('milan.checkCompatibility')}
                </>
              )}
            </Button>
          </div>

          {profiles.length < 2 && (
            <p className="text-center text-[#6B6560] text-sm mt-4">
              {language === 'hi' 
                ? 'अनुकूलता जांचने के लिए आपको कम से कम 2 प्रोफाइल चाहिए।'
                : 'You need at least 2 profiles to check compatibility.'}{' '}
              <button 
                onClick={() => navigate('/profiles')}
                className="text-[#C84B32] hover:underline"
              >
                {language === 'hi' ? 'और प्रोफाइल जोड़ें' : 'Add more profiles'}
              </button>
            </p>
          )}
        </div>

        {/* Results */}
        <AnimatePresence>
          {result && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="space-y-8"
            >
              {/* Main Score Card */}
              <div className="cosmic-card p-8 text-center relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-[#C84B32]/5 to-[#F5B942]/5" />
                
                <div className="relative z-10">
                  <div className="flex justify-center gap-8 mb-8">
                    {/* Profile 1 */}
                    <div className="text-center">
                      <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#C84B32] to-[#F5B942] flex items-center justify-center text-2xl font-bold text-white mx-auto mb-3">
                        {getInitials(result.profile1.name)}
                      </div>
                      <p className="text-[#282622] font-medium">{result.profile1.name}</p>
                      <p className="text-[#6B6560] text-xs">{result.profile1.vedic.moon_sign}</p>
                    </div>

                    {/* Heart with Score */}
                    <div className="flex flex-col items-center justify-center">
                      <div className="relative">
                        <Heart className="w-16 h-16 text-[#C84B32]" fill="#C84B32" />
                        <div className="absolute inset-0 flex items-center justify-center">
                          <span className="text-white font-bold text-sm">{result.guna_milan.total_score}</span>
                        </div>
                      </div>
                      <p className="text-[#6B6560] text-xs mt-1">of 36</p>
                    </div>

                    {/* Profile 2 */}
                    <div className="text-center">
                      <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#C84B32] to-[#F5B942] flex items-center justify-center text-2xl font-bold text-white mx-auto mb-3">
                        {getInitials(result.profile2.name)}
                      </div>
                      <p className="text-[#282622] font-medium">{result.profile2.name}</p>
                      <p className="text-[#6B6560] text-xs">{result.profile2.vedic.moon_sign}</p>
                    </div>
                  </div>

                  {/* Score Display */}
                  <div className="mb-6">
                    <p className={`text-6xl font-['Fraunces'] font-bold ${getScoreColor(result.guna_milan.percentage)}`}>
                      {result.guna_milan.percentage}%
                    </p>
                    <p className="text-[#282622] text-2xl font-['Fraunces'] mt-2">
                      {result.guna_milan.verdict}
                    </p>
                  </div>

                  <p className="text-[#6B6560] max-w-xl mx-auto">
                    {result.guna_milan.verdict_detail}
                  </p>

                  {/* Manglik Status */}
                  <div className="flex justify-center gap-8 mt-6">
                    <div className="text-center">
                      <p className="text-[#6B6560] text-xs mb-1">Manglik Status</p>
                      <p className="text-[#282622]">{result.profile1.name}: {result.guna_milan.manglik_status.groom}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-[#6B6560] text-xs mb-1">Manglik Status</p>
                      <p className="text-[#282622]">{result.profile2.name}: {result.guna_milan.manglik_status.bride}</p>
                    </div>
                  </div>

                  {/* Share Compatibility */}
                  <div className="flex justify-center mt-6">
                    <ShareButton 
                      type="compatibility"
                      data={{
                        person1: result.profile1.name,
                        person2: result.profile2.name,
                        score: result.guna_milan.percentage
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* Ashtakoot Details */}
              <div className="cosmic-card p-8">
                <h3 className="font-['Fraunces'] text-2xl text-[#282622] mb-6 text-center">
                  Ashtakoot Guna Milan
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {Object.entries(result.guna_milan.guna_details).map(([key, guna]) => (
                    <div 
                      key={key}
                      className="bg-[#FFF9EF] rounded-xl p-4 border border-[#282622]/10"
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <GunaIcon icon={GUNA_INFO[key]?.icon} />
                          <div>
                            <p className="text-[#282622] font-medium">{GUNA_INFO[key]?.name}</p>
                            <p className="text-[#6B6560] text-xs">{GUNA_INFO[key]?.description}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className={`text-lg font-bold ${guna.score === guna.max ? 'text-green-600' : guna.score >= guna.max/2 ? 'text-[#C84B32]' : 'text-red-600'}`}>
                            {guna.score}/{guna.max}
                          </p>
                        </div>
                      </div>
                      <div className="w-full bg-white rounded-full h-2">
                        <div 
                          className={`h-2 rounded-full ${guna.score === guna.max ? 'bg-green-400' : guna.score >= guna.max/2 ? 'bg-[#C84B32]' : 'bg-red-400'}`}
                          style={{ width: `${(guna.score / guna.max) * 100}%` }}
                        />
                      </div>
                      <div className="flex justify-between mt-2 text-xs text-[#6B6560]">
                        <span>{result.profile1.name}: {guna.groom}</span>
                        <span>{result.profile2.name}: {guna.bride}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* AI Analysis */}
              <div className="cosmic-card p-8">
                <h3 className="font-['Fraunces'] text-2xl text-[#282622] mb-6 text-center">
                  Cosmic Analysis
                </h3>
                
                <div className="space-y-6">
                  {/* Overall Analysis */}
                  <div className="bg-gradient-to-r from-[#C84B32]/10 to-[#F5B942]/10 rounded-xl p-6 border border-[#C84B32]/20">
                    <div className="flex items-center gap-2 mb-3">
                      <Sparkles className="w-5 h-5 text-[#C84B32]" />
                      <p className="text-[#C84B32] text-sm uppercase tracking-wider">Overall Compatibility</p>
                    </div>
                    <p className="text-[#282622] leading-relaxed">{result.ai_analysis.overall_analysis}</p>
                  </div>

                  {/* Grid of Analysis Points */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-[#FFF9EF] rounded-xl p-5 border border-[#282622]/10">
                      <div className="flex items-center gap-2 mb-2">
                        <Heart className="w-4 h-4 text-[#C84B32]" />
                        <p className="text-[#C84B32] text-xs uppercase tracking-wider">Emotional Bond</p>
                      </div>
                      <p className="text-[#6B6560] text-sm">{result.ai_analysis.emotional_bond}</p>
                    </div>

                    <div className="bg-[#FFF9EF] rounded-xl p-5 border border-[#282622]/10">
                      <div className="flex items-center gap-2 mb-2">
                        <Users className="w-4 h-4 text-[#C84B32]" />
                        <p className="text-[#C84B32] text-xs uppercase tracking-wider">Communication</p>
                      </div>
                      <p className="text-[#6B6560] text-sm">{result.ai_analysis.communication}</p>
                    </div>

                    <div className="bg-[#FFF9EF] rounded-xl p-5 border border-[#282622]/10">
                      <div className="flex items-center gap-2 mb-2">
                        <Sun className="w-4 h-4 text-orange-600" />
                        <p className="text-orange-600 text-xs uppercase tracking-wider">Family Life</p>
                      </div>
                      <p className="text-[#6B6560] text-sm">{result.ai_analysis.family_life}</p>
                    </div>

                    <div className="bg-[#FFF9EF] rounded-xl p-5 border border-[#282622]/10">
                      <div className="flex items-center gap-2 mb-2">
                        <Star className="w-4 h-4 text-green-600" />
                        <p className="text-green-600 text-xs uppercase tracking-wider">Financial Harmony</p>
                      </div>
                      <p className="text-[#6B6560] text-sm">{result.ai_analysis.financial_harmony}</p>
                    </div>
                  </div>

                  {/* Strengths & Challenges */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-green-500/10 rounded-xl p-5 border border-green-500/20">
                      <div className="flex items-center gap-2 mb-2">
                        <Check className="w-4 h-4 text-green-600" />
                        <p className="text-green-600 text-xs uppercase tracking-wider">Strengths</p>
                      </div>
                      <p className="text-[#6B6560] text-sm">{result.ai_analysis.strengths}</p>
                    </div>

                    <div className="bg-orange-500/10 rounded-xl p-5 border border-orange-500/20">
                      <div className="flex items-center gap-2 mb-2">
                        <AlertCircle className="w-4 h-4 text-orange-600" />
                        <p className="text-orange-600 text-xs uppercase tracking-wider">Challenges</p>
                      </div>
                      <p className="text-[#6B6560] text-sm">{result.ai_analysis.challenges}</p>
                    </div>
                  </div>

                  {/* Remedies */}
                  <div className="bg-[#C84B32]/10 rounded-xl p-6 border border-[#C84B32]/20">
                    <div className="flex items-center gap-2 mb-4">
                      <Moon className="w-5 h-5 text-[#C84B32]" />
                      <p className="text-[#C84B32] text-sm uppercase tracking-wider">Vedic Remedies</p>
                    </div>
                    <ul className="space-y-2">
                      {result.ai_analysis.remedies?.map((remedy, idx) => (
                        <li key={idx} className="flex items-start gap-3 text-[#6B6560] text-sm">
                          <Star className="w-4 h-4 text-[#C84B32] mt-0.5 flex-shrink-0" />
                          {remedy}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Auspicious Activities */}
                  <div className="text-center p-6 bg-gradient-to-r from-[#C84B32]/10 to-transparent rounded-xl border border-[#C84B32]/20">
                    <p className="text-[#C84B32] text-xs uppercase tracking-wider mb-2">Auspicious Activities</p>
                    <p className="text-[#282622]">{result.ai_analysis.auspicious_activities}</p>
                  </div>
                </div>
              </div>

              {/* Check Another */}
              <div className="flex flex-wrap justify-center gap-4">
                <Button
                  onClick={downloadPdf}
                  disabled={generatingPdf}
                  data-testid="download-kundli-milan-pdf"
                  className="bg-[#C84B32] hover:bg-[#E85D43] text-white px-6"
                >
                  {generatingPdf ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Download className="w-4 h-4 mr-2" />
                  )}
                  Download PDF Report
                </Button>
                <Button
                  onClick={() => { setResult(null); setProfile1(''); setProfile2(''); }}
                  variant="outline"
                  className="border-[#C84B32]/50 text-[#C84B32] hover:bg-[#C84B32]/10"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Check Another Pair
                </Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </main>
      <ShubhBottomNav />
    </div>
  );
};

export default KundliMilanPage;
